import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Users, Shield, Clock, Save, X, Globe, KeyRound, Copy, RefreshCw } from "lucide-react";

const SYS_ROLES = ["admin", "manager", "staff"];
const DEPARTMENTS = ["Management", "Service Crew", "Kitchen", "Delivery"];
const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function EmployeesPage() {
  const [activeTab, setActiveTab] = useState("employees");
  // Employees
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("current");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [form, setForm] = useState({ name: "", first_name: "", last_name: "", email: "", role: "staff", pin: "", phone: "", employee_start: "", employee_end: "", roles_wages: [], is_active: true, portal_access: false });
  // POS Roles
  const [posRoles, setPosRoles] = useState([]);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", department: "Service Crew", permission_rank: 100, is_active: true, tip_share: true });
  // Timesheet Rules
  const [tsRules, setTsRules] = useState(null);
  const [tsSaving, setTsSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [eRes, rRes, tRes] = await Promise.all([
        api.get("/employees"),
        api.get("/pos-roles"),
        api.get("/timesheet-rules"),
      ]);
      setEmployees(eRes.data);
      setPosRoles(rRes.data);
      setTsRules(tRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // === EMPLOYEES ===
  const filteredEmployees = employees.filter((e) => {
    const matchSearch = !search || (e.name || "").toLowerCase().includes(search.toLowerCase()) || (e.email || "").toLowerCase().includes(search.toLowerCase()) || (e.first_name || "").toLowerCase().includes(search.toLowerCase()) || (e.last_name || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || (statusFilter === "current" ? e.is_active !== false : e.is_active === false);
    return matchSearch && matchStatus;
  });

  const openCreateEmployee = () => {
    setEditing(null);
    setGeneratedPassword("");
    setForm({ name: "", first_name: "", last_name: "", email: "", role: "staff", pin: "", phone: "", employee_start: new Date().toISOString().slice(0, 10), employee_end: "", roles_wages: [{ pos_role_id: "", hourly_rate: 0 }], is_active: true, portal_access: false });
    setShowDialog(true);
  };

  const openEditEmployee = (emp) => {
    setEditing(emp);
    setGeneratedPassword("");
    const rw = emp.roles_wages?.length > 0 ? emp.roles_wages : [{ pos_role_id: "", hourly_rate: 0 }];
    setForm({ name: emp.name || "", first_name: emp.first_name || "", last_name: emp.last_name || "", email: emp.email, role: emp.role, pin: emp.pin || "", phone: emp.phone || "", employee_start: emp.employee_start || "", employee_end: emp.employee_end || "", roles_wages: rw, is_active: emp.is_active !== false, portal_access: emp.portal_access || false });
    setShowDialog(true);
  };

  const saveEmployee = async () => {
    const payload = { ...form, name: form.name || `${form.first_name} ${form.last_name}`.trim() };
    try {
      let res;
      if (editing) {
        res = await api.put(`/employees/${editing.user_id}`, payload);
        toast.success("Employee updated");
      } else {
        res = await api.post("/employees", payload);
        toast.success("Employee created");
      }
      if (res.data.generated_password) {
        setGeneratedPassword(res.data.generated_password);
      } else {
        setShowDialog(false);
        fetchAll();
      }
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const resetPortalPassword = async (userId) => {
    try {
      const res = await api.post(`/employees/${userId}/reset-portal-password`);
      setGeneratedPassword(res.data.generated_password);
      toast.success("Password reset — share the new temporary password with the employee");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reset password"); }
  };

  const copyPassword = (pw) => {
    navigator.clipboard.writeText(pw);
    toast.success("Password copied to clipboard");
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Deleted"); fetchAll(); }
    catch { toast.error("Failed to delete"); }
  };

  const addRoleWage = () => setForm((p) => ({ ...p, roles_wages: [...p.roles_wages, { pos_role_id: "", hourly_rate: 0 }] }));
  const removeRoleWage = (idx) => setForm((p) => ({ ...p, roles_wages: p.roles_wages.filter((_, i) => i !== idx) }));
  const updateRoleWage = (idx, key, val) => setForm((p) => { const rw = [...p.roles_wages]; rw[idx] = { ...rw[idx], [key]: val }; return { ...p, roles_wages: rw }; });

  const getRoleName = (roleId) => posRoles.find((r) => r.pos_role_id === roleId)?.name || "";
  const getEmployeeRoles = (emp) => {
    if (emp.roles_wages?.length > 0) return emp.roles_wages.map((rw) => getRoleName(rw.pos_role_id)).filter(Boolean).join(", ");
    return emp.role || "-";
  };

  // === POS ROLES ===
  const openCreateRole = () => { setEditingRole(null); setRoleForm({ name: "", department: "Service Crew", permission_rank: 100, is_active: true, tip_share: true }); setShowRoleDialog(true); };
  const openEditRole = (r) => { setEditingRole(r); setRoleForm({ name: r.name, department: r.department || "Service Crew", permission_rank: r.permission_rank || 100, is_active: r.is_active !== false, tip_share: r.tip_share !== false }); setShowRoleDialog(true); };
  const saveRole = async () => {
    if (!roleForm.name.trim()) { toast.error("Role name required"); return; }
    try {
      const payload = { ...roleForm, permission_rank: parseInt(roleForm.permission_rank) || 100 };
      if (editingRole) { await api.put(`/pos-roles/${editingRole.pos_role_id}`, payload); toast.success("Role updated"); }
      else { await api.post("/pos-roles", payload); toast.success("Role created"); }
      setShowRoleDialog(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };
  const deleteRole = async (id) => {
    if (!window.confirm("Delete this POS role?")) return;
    try { await api.delete(`/pos-roles/${id}`); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  // === TIMESHEET RULES ===
  const updateRule = (key, val) => setTsRules((p) => ({ ...p, [key]: val }));
  const saveRules = async () => {
    setTsSaving(true);
    try {
      const res = await api.put("/timesheet-rules", tsRules);
      setTsRules(res.data);
      toast.success("Timesheet rules saved");
    } catch { toast.error("Failed to save rules"); }
    finally { setTsSaving(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  const tabs = [
    { id: "employees", label: "Employees", icon: Users, count: employees.length },
    { id: "roles", label: "POS Roles", icon: Shield, count: posRoles.length },
    { id: "timesheet", label: "Timesheet Rules", icon: Clock },
  ];

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="employees-page">
      {/* Sidebar */}
      <div className="w-52 bg-white border-r border-slate-200 p-4 flex-shrink-0" data-testid="emp-sidebar">
        <h2 className="font-heading text-sm font-bold text-slate-900 mb-4 flex items-center gap-2"><Users className="h-4 w-4" /> Employees</h2>
        <nav className="space-y-1">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${activeTab === t.id ? "bg-[var(--brand-primary)] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`emp-tab-${t.id}`}>
              <span className="flex items-center gap-2"><t.icon className="h-3.5 w-3.5" /> {t.label}</span>
              {t.count !== undefined && <Badge variant={activeTab === t.id ? "secondary" : "outline"} className="text-[10px] h-5 min-w-[20px] justify-center">{t.count}</Badge>}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* === EMPLOYEES TAB === */}
        {activeTab === "employees" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="employees-title">Employees</h1>
              <Button onClick={openCreateEmployee} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-employee-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Employee
              </Button>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search employees..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="employees-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="emp-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">View All</SelectItem>
                  <SelectItem value="current">View Current</SelectItem>
                  <SelectItem value="former">View Former</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="employees-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Last Name</TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No employees found.</TableCell></TableRow>
                  ) : filteredEmployees.map((emp) => (
                    <TableRow key={emp.user_id} data-testid={`emp-row-${emp.user_id}`}>
                      <TableCell className="font-medium">{emp.last_name || emp.name?.split(" ").slice(-1)[0] || "-"}</TableCell>
                      <TableCell>{emp.first_name || emp.name?.split(" ")[0] || "-"}</TableCell>
                      <TableCell className="text-slate-500 text-sm truncate max-w-[160px]">{emp.email || "-"}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{emp.phone || "-"}</TableCell>
                      <TableCell><span className="text-sm text-slate-700">{getEmployeeRoles(emp)}</span></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant={emp.is_active !== false ? "default" : "secondary"} className="text-xs">{emp.is_active !== false ? "Current" : "Former"}</Badge>
                          {emp.portal_access && <Badge className="text-[10px] bg-blue-100 text-blue-700">Portal</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditEmployee(emp)} data-testid={`edit-emp-${emp.user_id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteEmployee(emp.user_id)} data-testid={`del-emp-${emp.user_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* === POS ROLES TAB === */}
        {activeTab === "roles" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="roles-title">POS Roles</h1>
              <Button onClick={openCreateRole} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-role-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Role
              </Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="roles-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Permission Rank</TableHead>
                    <TableHead className="text-center">Active</TableHead>
                    <TableHead className="text-center">Tip Share</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posRoles.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No POS roles configured.</TableCell></TableRow>
                  ) : posRoles.map((r) => (
                    <TableRow key={r.pos_role_id} data-testid={`role-row-${r.pos_role_id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-slate-500">{r.department}</TableCell>
                      <TableCell className="text-right font-mono">{r.permission_rank}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant={r.is_active ? "default" : "secondary"} className="text-xs">{r.is_active ? "Active" : "Inactive"}</Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">{r.tip_share ? "$" : "-"}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditRole(r)} data-testid={`edit-role-${r.pos_role_id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteRole(r.pos_role_id)} data-testid={`del-role-${r.pos_role_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <button onClick={openCreateRole} className="mt-3 text-sm text-[var(--brand-primary)] hover:underline font-medium">+ Role</button>
          </div>
        )}

        {/* === TIMESHEET RULES TAB === */}
        {activeTab === "timesheet" && tsRules && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="timesheet-title">Timesheet Rules</h1>
              <Button onClick={saveRules} disabled={tsSaving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-timesheet-btn">
                <Save className="h-4 w-4 mr-1" /> {tsSaving ? "Saving..." : "Save"}
              </Button>
            </div>

            {/* Hours & Overtime */}
            <div className="bg-white border border-slate-200 rounded-lg mb-4" data-testid="timesheet-rules-section">
              <div className="border-b border-slate-200 px-6 py-3 bg-slate-50">
                <h2 className="font-heading text-base font-semibold text-slate-900">Hours & Overtime</h2>
              </div>
              <div className="px-6 py-4 space-y-0">
                {[
                  { key: "max_regular_hours_day", label: "Maximum regular hours per day", type: "number", step: "0.5" },
                  { key: "max_overtime_hours_day", label: "Maximum overtime hours per day", type: "number", step: "0.5" },
                  { key: "overtime_multiplier", label: "Overtime rate multiplier", type: "number", step: "0.1" },
                  { key: "doubletime_multiplier", label: "Doubletime rate multiplier", type: "number", step: "0.1" },
                  { key: "max_regular_hours_week", label: "Maximum regular hours per week", type: "number", step: "0.5" },
                ].map(({ key, label, type, step }) => (
                  <div key={key} className="flex items-center justify-between py-3 border-b border-slate-100">
                    <Label className="text-sm text-slate-700">{label}</Label>
                    <Input type={type} step={step} value={tsRules[key] ?? ""} onChange={(e) => updateRule(key, parseFloat(e.target.value) || 0)} className="w-28 text-right" data-testid={`ts-${key}`} />
                  </div>
                ))}
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Consider seventh-worked day as overtime</Label>
                  <Switch checked={tsRules.consider_seventh_day_overtime || false} onCheckedChange={(v) => updateRule("consider_seventh_day_overtime", v)} data-testid="ts-consider_seventh_day_overtime" />
                </div>
              </div>
            </div>

            {/* Clock-in/out Rules */}
            <div className="bg-white border border-slate-200 rounded-lg mb-4">
              <div className="border-b border-slate-200 px-6 py-3 bg-slate-50">
                <h2 className="font-heading text-base font-semibold text-slate-900">Clock-in / Clock-out</h2>
              </div>
              <div className="px-6 py-4 space-y-0">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Allow clock-in before shift</Label>
                  <Switch checked={tsRules.allow_clock_in_before_shift || false} onCheckedChange={(v) => updateRule("allow_clock_in_before_shift", v)} data-testid="ts-allow_clock_in_before_shift" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Enable limit clock-in before shift</Label>
                  <Switch checked={tsRules.enable_limit_clockin_before_shift || false} onCheckedChange={(v) => updateRule("enable_limit_clockin_before_shift", v)} data-testid="ts-enable_limit_clockin_before_shift" />
                </div>
                {tsRules.enable_limit_clockin_before_shift && (
                  <div className="flex items-center justify-between py-3 border-b border-slate-100 bg-slate-50 px-4 -mx-6 px-6">
                    <Label className="text-sm text-slate-600">Clock-in before shift limit time</Label>
                    <Input type="time" value={tsRules.clockin_before_shift_limit_time || "07:00"} onChange={(e) => updateRule("clockin_before_shift_limit_time", e.target.value)} className="w-28" data-testid="ts-clockin_before_shift_limit_time" />
                  </div>
                )}
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Require manager approval for late clock-in</Label>
                  <Switch checked={tsRules.require_manager_approval_late_clockin || false} onCheckedChange={(v) => updateRule("require_manager_approval_late_clockin", v)} data-testid="ts-require_manager_approval_late_clockin" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Prevent employee clock-in before break end</Label>
                  <Switch checked={tsRules.prevent_clockin_before_break_end || false} onCheckedChange={(v) => updateRule("prevent_clockin_before_break_end", v)} data-testid="ts-prevent_clockin_before_break_end" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Enable auto clock-out</Label>
                  <Switch checked={tsRules.enable_auto_clock_out || false} onCheckedChange={(v) => updateRule("enable_auto_clock_out", v)} data-testid="ts-enable_auto_clock_out" />
                </div>
              </div>
            </div>

            {/* Break Rules */}
            <div className="bg-white border border-slate-200 rounded-lg mb-4">
              <div className="border-b border-slate-200 px-6 py-3 bg-slate-50">
                <h2 className="font-heading text-base font-semibold text-slate-900">Breaks</h2>
              </div>
              <div className="px-6 py-4 space-y-0">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Calculate Paid/Unpaid breaks by Rules</Label>
                  <Switch checked={tsRules.calculate_breaks_by_rules || false} onCheckedChange={(v) => updateRule("calculate_breaks_by_rules", v)} data-testid="ts-calculate_breaks_by_rules" />
                </div>
                {tsRules.calculate_breaks_by_rules && (
                  <div className="flex items-center justify-between py-3 border-b border-slate-100 bg-slate-50 -mx-6 px-6">
                    <Label className="text-sm text-slate-600">Break lengths (minutes, comma-separated)</Label>
                    <Input value={tsRules.break_lengths || ""} onChange={(e) => updateRule("break_lengths", e.target.value)} placeholder="e.g. 15,30,60" className="w-36 text-right" data-testid="ts-break_lengths" />
                  </div>
                )}
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Employee declares break type</Label>
                  <Switch checked={tsRules.employee_declares_break_type || false} onCheckedChange={(v) => updateRule("employee_declares_break_type", v)} data-testid="ts-employee_declares_break_type" />
                </div>
              </div>
            </div>

            {/* Payroll & Display */}
            <div className="bg-white border border-slate-200 rounded-lg mb-4">
              <div className="border-b border-slate-200 px-6 py-3 bg-slate-50">
                <h2 className="font-heading text-base font-semibold text-slate-900">Payroll & Display</h2>
              </div>
              <div className="px-6 py-4 space-y-0">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Display Declared Tips in Payroll</Label>
                  <Switch checked={tsRules.display_declared_tips_payroll || false} onCheckedChange={(v) => updateRule("display_declared_tips_payroll", v)} data-testid="ts-display_declared_tips_payroll" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Display Payment Tips in Payroll</Label>
                  <Switch checked={tsRules.display_payment_tips_payroll || false} onCheckedChange={(v) => updateRule("display_payment_tips_payroll", v)} data-testid="ts-display_payment_tips_payroll" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">12 Hour clock</Label>
                  <Switch checked={tsRules.use_12_hour_clock || false} onCheckedChange={(v) => updateRule("use_12_hour_clock", v)} data-testid="ts-use_12_hour_clock" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Week starts on</Label>
                  <Select value={tsRules.week_starts_on || "Sunday"} onValueChange={(v) => updateRule("week_starts_on", v)}>
                    <SelectTrigger className="w-36" data-testid="ts-week_starts_on"><SelectValue /></SelectTrigger>
                    <SelectContent>{WEEK_DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between py-3">
                  <Label className="text-sm text-slate-700">Day starts on time</Label>
                  <Input type="time" value={tsRules.day_starts_on_time || "00:00"} onChange={(e) => updateRule("day_starts_on_time", e.target.value)} className="w-28" data-testid="ts-day_starts_on_time" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Employee Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Employee" : "New Employee"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">{editing ? "Update employee details and roles" : "Add a new team member"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Employee Info */}
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Employee Info</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">First Name *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Last Name *</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="emp-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">PIN *</Label>
                <Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} placeholder="4-digit" maxLength={6} data-testid="emp-pin" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Start Date</Label>
                <Input type="date" value={form.employee_start} onChange={(e) => setForm({ ...form, employee_start: e.target.value })} data-testid="emp-start-date" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">End Date</Label>
                <Input type="date" value={form.employee_end} onChange={(e) => setForm({ ...form, employee_end: e.target.value })} data-testid="emp-end-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div>
                <Label className="text-xs text-slate-500">System Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="emp-sys-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{SYS_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-4">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="emp-active-switch" />
                <Label className="text-sm">{form.is_active ? "Active" : "Inactive"}</Label>
              </div>
            </div>

            {/* Portal Access */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Portal Access</h3>
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Dashboard Portal Login</p>
                    <p className="text-[10px] text-slate-400">Allow this employee to log into the admin portal with email & password</p>
                  </div>
                </div>
                <Switch checked={form.portal_access} onCheckedChange={(v) => setForm({ ...form, portal_access: v })} data-testid="emp-portal-access" />
              </div>
              {form.portal_access && editing && !generatedPassword && (
                <div className="mt-2 flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => resetPortalPassword(editing.user_id)} className="text-xs" data-testid="reset-portal-pw-btn">
                    <RefreshCw className="h-3 w-3 mr-1" /> Reset Portal Password
                  </Button>
                </div>
              )}
              {generatedPassword && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="generated-password-display">
                  <div className="flex items-center gap-2 mb-1">
                    <KeyRound className="h-4 w-4 text-amber-600" />
                    <p className="text-xs font-semibold text-amber-800">Temporary Password (share with employee)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-white border border-amber-300 rounded px-3 py-1.5 text-sm font-mono font-bold text-slate-900 select-all" data-testid="temp-password-value">{generatedPassword}</code>
                    <Button variant="ghost" size="sm" onClick={() => copyPassword(generatedPassword)} className="h-8 text-xs"><Copy className="h-3 w-3 mr-1" /> Copy</Button>
                  </div>
                  <p className="text-[10px] text-amber-600 mt-1.5">Employee will be required to change this on first login.</p>
                </div>
              )}
            </div>

            {/* Roles & Wages */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Roles & Wages</h3>
              <div className="space-y-2">
                {form.roles_wages.map((rw, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg" data-testid={`rw-${idx}`}>
                    <Select value={rw.pos_role_id || "none"} onValueChange={(v) => updateRoleWage(idx, "pos_role_id", v === "none" ? "" : v)}>
                      <SelectTrigger className="flex-1" data-testid={`rw-role-${idx}`}><SelectValue placeholder="Select Role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select Role</SelectItem>
                        {posRoles.filter((r) => r.is_active).map((r) => <SelectItem key={r.pos_role_id} value={r.pos_role_id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-400">$</span>
                      <Input type="number" step="0.01" value={rw.hourly_rate || ""} onChange={(e) => updateRoleWage(idx, "hourly_rate", parseFloat(e.target.value) || 0)} className="w-24" placeholder="Rate" data-testid={`rw-rate-${idx}`} />
                      <span className="text-xs text-slate-400">/hr</span>
                    </div>
                    {form.roles_wages.length > 1 && (
                      <button onClick={() => removeRoleWage(idx)} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
                <button onClick={addRoleWage} className="text-sm text-[var(--brand-primary)] hover:underline font-medium" data-testid="add-role-wage-btn">+ Add Role</button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveEmployee} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="employee-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POS Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingRole ? "Edit POS Role" : "New POS Role"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Configure a role for POS terminal access</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Role Name</Label>
              <Input value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="e.g., Chef, Front End, Manager" data-testid="role-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Department</Label>
                <Select value={roleForm.department} onValueChange={(v) => setRoleForm({ ...roleForm, department: v })}>
                  <SelectTrigger data-testid="role-dept-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Permission Rank</Label>
                <Input type="number" value={roleForm.permission_rank} onChange={(e) => setRoleForm({ ...roleForm, permission_rank: e.target.value })} data-testid="role-rank-input" />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={roleForm.is_active} onCheckedChange={(v) => setRoleForm({ ...roleForm, is_active: v })} data-testid="role-active-switch" />
                <Label className="text-sm">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={roleForm.tip_share} onCheckedChange={(v) => setRoleForm({ ...roleForm, tip_share: v })} data-testid="role-tip-switch" />
                <Label className="text-sm">Tip Share ($)</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button onClick={saveRole} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="role-save-btn">{editingRole ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
